'use client';

import { type ReactNode } from 'react';

import { DialogContent } from '@ps/ui/atoms/dialog';
import { cn } from '@ps/ui/utils';

interface Props {
  children: ReactNode;
  className?: string;
}

/**
 * Shared shell for the Create / Edit channel modals. The form is taller than a
 * small viewport, and the base centered dialog neither caps its height nor
 * scrolls — so on mobile it clips. Here:
 * - **Mobile** (`max-sm`): a full-screen sheet (`inset-0`, full height, no
 *   rounded corners/border, centering translate cleared) that scrolls.
 * - **Desktop** (`sm`): the usual centered card, capped at `85dvh` and
 *   scrolling internally.
 *
 * `max-sm:pb-12` replaces the base `p-6` bottom on mobile so the trailing Save
 * button clears Android's 3-button navigation bar. `h-dvh` is the WEBVIEW's
 * height, and the native shell only reserves a flat 20px below it — against a
 * 48dp 3-button bar that leaves ~28px of the sheet physically behind the bar, so
 * 24px of padding left the button slightly cropped. `env(safe-area-inset-bottom)`
 * is no help: it reads 0 inside the Android WebView. On every other target
 * (`web`, iOS, Android gesture nav) 24px already sufficed, so the extra is just
 * scroll room at the end of a sheet that scrolls anyway — the sheet's own
 * background fills it, so nothing shows through.
 */
export function ChannelFormDialogContent({ children, className }: Props) {
  return (
    <DialogContent
      showCloseButton
      className={cn(
        'text-content-1 border-line bg-surface-1 overflow-y-auto',
        'sm:max-h-[85dvh] sm:max-w-md',
        'max-sm:inset-0 max-sm:h-dvh max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0 max-sm:pb-12',
        className,
      )}
    >
      {children}
    </DialogContent>
  );
}
